/**
 * Currency options for the profile "Display currency" picker, and a small
 * helper to state a memo's denomination plainly.
 *
 * Kept in lockstep with the backend `common/currency.ts` COMMON_CURRENCIES
 * list (the two can't share a module across the interface/methods boundary).
 * The backend is authoritative — it validates the stored value — so this list
 * only needs to offer the same set of choices.
 */
export interface CurrencyOption {
  /** ISO 4217 code, stored on the profile and the memo. */
  code: string;
  /** Human name for the picker. */
  name: string;
  /**
   * Glyph printed in front of a figure. Kept in lockstep with the backend
   * `common/currency.ts` glyphs so a chart axis (computed client-side from raw
   * numbers) matches the tables (formatted server-side).
   */
  glyph: string;
}

export const CURRENCIES: CurrencyOption[] = [
  { code: 'USD', name: 'US dollars', glyph: '$' },
  { code: 'EUR', name: 'euros', glyph: '€' },
  { code: 'GBP', name: 'British pounds', glyph: '£' },
  { code: 'CAD', name: 'Canadian dollars', glyph: 'C$' },
  { code: 'AUD', name: 'Australian dollars', glyph: 'A$' },
  { code: 'JPY', name: 'Japanese yen', glyph: '¥' },
  { code: 'CNY', name: 'Chinese yuan', glyph: '¥' },
  { code: 'INR', name: 'Indian rupees', glyph: '₹' },
  { code: 'CHF', name: 'Swiss francs', glyph: 'CHF' },
  { code: 'SEK', name: 'Swedish kronor', glyph: 'kr' },
  { code: 'NOK', name: 'Norwegian kroner', glyph: 'kr' },
  { code: 'DKK', name: 'Danish kroner', glyph: 'kr' },
  { code: 'NZD', name: 'New Zealand dollars', glyph: 'NZ$' },
  { code: 'SGD', name: 'Singapore dollars', glyph: 'S$' },
  { code: 'HKD', name: 'Hong Kong dollars', glyph: 'HK$' },
  { code: 'AED', name: 'UAE dirhams', glyph: 'AED' },
  { code: 'SAR', name: 'Saudi riyals', glyph: 'SAR' },
  { code: 'ZAR', name: 'South African rand', glyph: 'R' },
  { code: 'BRL', name: 'Brazilian reais', glyph: 'R$' },
  { code: 'MXN', name: 'Mexican pesos', glyph: 'MX$' },
  { code: 'KRW', name: 'South Korean won', glyph: '₩' },
  { code: 'PLN', name: 'Polish złoty', glyph: 'zł' },
  { code: 'ILS', name: 'Israeli shekels', glyph: '₪' },
  { code: 'TRY', name: 'Turkish lira', glyph: '₺' },
];

const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));

/** The glyph for a code, defaulting to "$" for USD / unknown. */
export function currencyGlyphFor(code: string | null | undefined): string {
  if (!code) return '$';
  return BY_CODE.get(code.toUpperCase())?.glyph ?? '$';
}

/**
 * A plain denomination statement, e.g. "euros (EUR)". Falls back to the raw
 * code for a currency this list doesn't know (a memo generated with a value
 * the picker has since dropped), so it always reads sensibly.
 */
export function currencyDisplayLabel(code: string | null | undefined): string {
  if (!code) return '';
  const meta = BY_CODE.get(code.toUpperCase());
  return meta ? `${meta.name} (${meta.code})` : code.toUpperCase();
}
